import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  BanknotesIcon,
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  ReceiptPercentIcon,
  ScaleIcon,
  TagIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { badgeClass, formatNumber, getApiErrorMessage, secondaryButtonClass } from '../../components/ui/FormControls';
import masterDataService, { MasterCount, MasterKey } from '../../services/master-data.service';
import { cn } from '../../lib/utils';

interface MasterDefinition {
  key: MasterKey;
  title: string;
  // "1 plant", "3 plants"
  singular: string;
  plural: string;
  description: string;
  route: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  // The master that has to exist before this one can be added
  needs?: MasterKey;
  // What to do, in the setup guide
  step: string;
}

// In the order they are set up: each one builds on those before it
const MASTERS: MasterDefinition[] = [
  {
    key: 'companies',
    title: 'Companies',
    singular: 'company',
    plural: 'companies',
    description: 'Legal entities with their address, GST, PAN and bank details.',
    route: '/master-data/companies',
    icon: BuildingOffice2Icon,
    step: 'Add your company and mark it as primary',
  },
  {
    key: 'plants',
    title: 'Plants',
    singular: 'plant',
    plural: 'plants',
    description: 'Factories, warehouses and service locations of each company.',
    route: '/master-data/plants',
    icon: BuildingOfficeIcon,
    needs: 'companies',
    step: 'Add the plants and warehouses of the company',
  },
  {
    key: 'costCenters',
    title: 'Cost Centers',
    singular: 'cost center',
    plural: 'cost centers',
    description: 'Where costs are collected in a plant, with budgets.',
    route: '/master-data/cost-centers',
    icon: BanknotesIcon,
    needs: 'plants',
    step: 'Add cost centers for each plant',
  },
  {
    key: 'workCenters',
    title: 'Work Centers',
    singular: 'work center',
    plural: 'work centers',
    description: 'Machines, lines and labor groups, with capacity and hourly rates.',
    route: '/master-data/work-centers',
    icon: WrenchScrewdriverIcon,
    needs: 'plants',
    step: 'Add the machines and lines of each plant',
  },
  {
    key: 'uoms',
    title: 'Units of Measure',
    singular: 'unit',
    plural: 'units',
    description: 'Units for quantity, weight, length and more, with conversions.',
    route: '/master-data/uoms',
    icon: ScaleIcon,
    step: 'Add the units you buy, stock and sell in',
  },
  {
    key: 'itemTypes',
    title: 'Item Types',
    singular: 'item type',
    plural: 'item types',
    description: 'Raw material, semi-finished, finished goods and how each is tracked.',
    route: '/master-data/item-types',
    icon: CubeIcon,
    step: 'Add item types such as raw material and finished goods',
  },
  {
    key: 'itemAttributes',
    title: 'Item Attributes',
    singular: 'attribute',
    plural: 'attributes',
    description: 'Extra properties of items, such as grade, colour or size.',
    route: '/master-data/item-attributes',
    icon: TagIcon,
    step: 'Add the item properties you want to record',
  },
  {
    key: 'gstRates',
    title: 'GST Rates',
    singular: 'GST rate',
    plural: 'GST rates',
    description: 'The GST rates offered on HSN codes, orders and invoices.',
    route: '/master-data/gst-rates',
    icon: ReceiptPercentIcon,
    step: 'Review the GST rates and choose the default',
  },
  {
    key: 'boms',
    title: 'Bills of Materials',
    singular: 'bill of materials',
    plural: 'bills of materials',
    description: 'What goes into each product, with versions and costs.',
    route: '/master-data/boms',
    icon: ClipboardDocumentListIcon,
    step: 'Add a bill of materials for each product you make',
  },
];

const titleOf = (key: MasterKey) => MASTERS.find((master) => master.key === key)?.title ?? key;

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const cardClass = 'rounded-lg bg-white shadow-sm ring-1 ring-gray-200';

const MasterDataDashboard: React.FC = () => {
  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['master-data', 'overview'],
    queryFn: () => masterDataService.getOverview(),
  });

  const counts = new Map<MasterKey, MasterCount>((data?.masters ?? []).map((master) => [master.key, master]));
  const totalOf = (key: MasterKey) => counts.get(key)?.total ?? 0;

  const done = MASTERS.filter((master) => totalOf(master.key) > 0).length;
  const records = MASTERS.reduce((sum, master) => sum + totalOf(master.key), 0);
  // The first master with nothing in it whose prerequisite is there
  const nextStep = MASTERS.find((master) => totalOf(master.key) === 0 && (!master.needs || totalOf(master.needs) > 0));

  const attention: { text: string; route: string; action: string }[] = [];
  if (data) {
    const plural = (count: number, one: string, many: string) => `${formatNumber(count)} ${count === 1 ? one : many}`;
    if (totalOf('companies') > 0 && !data.primaryCompany) {
      attention.push({
        text: 'No active company is marked as primary. Invoices take the seller details from the primary company.',
        route: '/master-data/companies',
        action: 'Open companies',
      });
    }
    if (data.attention.companiesWithoutPlants > 0) {
      attention.push({
        text: `${plural(data.attention.companiesWithoutPlants, 'active company has', 'active companies have')} no active plant.`,
        route: '/master-data/plants',
        action: 'Open plants',
      });
    }
    if (data.attention.plantsWithoutWorkCenters > 0) {
      attention.push({
        text: `${plural(data.attention.plantsWithoutWorkCenters, 'active plant has', 'active plants have')} no active work center.`,
        route: '/master-data/work-centers',
        action: 'Open work centers',
      });
    }
    if (data.attention.workCentersDown > 0) {
      attention.push({
        text: `${plural(data.attention.workCentersDown, 'work center is', 'work centers are')} under maintenance or broken down.`,
        route: '/master-data/work-centers',
        action: 'Open work centers',
      });
    }
    if (data.attention.bomDrafts > 0) {
      attention.push({
        text: `${plural(data.attention.bomDrafts, 'bill of materials is', 'bills of materials are')} still in draft.`,
        route: '/master-data/boms',
        action: 'Open bills of materials',
      });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Master Data</h1>
          <p className="mt-1 text-sm text-gray-500">
            The companies, plants, resources, units and item definitions that the rest of the system builds on.
          </p>
        </div>
        <button type="button" onClick={() => refetch()} disabled={isFetching} className={secondaryButtonClass}>
          <ArrowPathIcon className={cn('-ml-0.5 mr-2 h-4 w-4', isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {isError ? (
        <div className={cn(cardClass, 'p-10 text-center')}>
          <p className="text-sm text-gray-700">{getApiErrorMessage(error, 'The master data overview could not be loaded.')}</p>
          <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'mt-4')}>
            Try again
          </button>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <div className={cn(cardClass, 'p-4')}>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Masters set up</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{isLoading ? '—' : `${done} of ${MASTERS.length}`}</p>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
                <div className="h-full rounded-full bg-indigo-600" style={{ width: `${(done / MASTERS.length) * 100}%` }} />
              </div>
            </div>
            <div className={cn(cardClass, 'p-4')}>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Records</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{isLoading ? '—' : formatNumber(records)}</p>
              <p className="mt-1 text-xs text-gray-500">Across all {MASTERS.length} masters</p>
            </div>
            <div className={cn(cardClass, 'p-4')}>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Primary company</p>
              <p className="mt-1 truncate text-2xl font-semibold text-gray-900" title={data?.primaryCompany?.name}>
                {isLoading ? '—' : data?.primaryCompany?.name ?? 'Not set'}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {data?.primaryCompany ? `Code ${data.primaryCompany.code}` : 'Its details are used on invoices'}
              </p>
            </div>
            <div className={cn(cardClass, 'p-4')}>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Needs attention</p>
              <p className={cn('mt-1 text-2xl font-semibold', attention.length > 0 ? 'text-amber-600' : 'text-gray-900')}>
                {isLoading ? '—' : attention.length}
              </p>
              <p className="mt-1 text-xs text-gray-500">{attention.length > 0 ? 'Listed below' : 'Nothing to follow up'}</p>
            </div>
          </div>

          {/* The masters */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {MASTERS.map((master) => {
              const count = counts.get(master.key);
              const total = count?.total ?? 0;
              const inactive = total - (count?.active ?? 0);
              const Icon = master.icon;
              return (
                <Link
                  key={master.key}
                  to={master.route}
                  className={cn(
                    cardClass,
                    'group flex flex-col p-4 transition hover:shadow-md hover:ring-indigo-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="rounded-lg bg-indigo-50 p-2">
                      <Icon className="h-5 w-5 text-indigo-600" />
                    </div>
                    {isLoading ? (
                      <div className="h-8 w-10 animate-pulse rounded bg-gray-100" />
                    ) : (
                      <p className={cn('text-2xl font-semibold tabular-nums', total > 0 ? 'text-gray-900' : 'text-gray-300')}>
                        {formatNumber(total)}
                      </p>
                    )}
                  </div>
                  <h2 className="mt-3 text-sm font-semibold text-gray-900 group-hover:text-indigo-700">{master.title}</h2>
                  <p className="mt-1 flex-1 text-sm text-gray-500">{master.description}</p>
                  <div className="mt-3 flex items-center justify-between gap-2 border-t border-gray-100 pt-3 text-xs text-gray-500">
                    <span className="truncate">
                      {isLoading
                        ? ' '
                        : total === 0
                          ? 'None added yet'
                          : `${inactive > 0 ? `${formatNumber(inactive)} inactive · ` : ''}Updated ${formatDate(count?.lastUpdated as string)}`}
                    </span>
                    <span className="inline-flex shrink-0 items-center font-medium text-indigo-600 group-hover:text-indigo-500">
                      {total === 0 ? 'Add' : 'Manage'}
                      <ChevronRightIcon className="ml-0.5 h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            {/* Setup guide */}
            <section className={cn(cardClass, 'xl:col-span-2')}>
              <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
                <div className="flex items-center gap-2">
                  <AdjustmentsHorizontalIcon className="h-5 w-5 text-gray-400" />
                  <h2 className="text-sm font-semibold text-gray-900">Setup guide</h2>
                </div>
                {!isLoading && (
                  <span className={cn(badgeClass, done === MASTERS.length ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-gray-50 text-gray-600 ring-gray-500/20')}>
                    {done === MASTERS.length ? 'All set up' : `${MASTERS.length - done} to go`}
                  </span>
                )}
              </div>
              <ol className="divide-y divide-gray-100">
                {MASTERS.map((master, index) => {
                  const total = totalOf(master.key);
                  const isDone = total > 0;
                  const blocked = !isDone && !!master.needs && totalOf(master.needs) === 0;
                  const isNext = nextStep?.key === master.key;
                  return (
                    <li key={master.key} className={cn('flex items-center gap-3 px-4 py-3', isNext && 'bg-indigo-50/50')}>
                      <span
                        className={cn(
                          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                          isDone ? 'bg-green-100 text-green-700' : isNext ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500',
                        )}
                      >
                        {isDone ? <CheckIcon className="h-4 w-4" /> : index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className={cn('text-sm font-medium', isDone ? 'text-gray-500' : 'text-gray-900')}>{master.step}</p>
                        <p className="text-xs text-gray-500">
                          {isLoading
                            ? ' '
                            : isDone
                              ? `${formatNumber(total)} ${total === 1 ? master.singular : master.plural} added`
                              : blocked
                                ? `Needs ${titleOf(master.needs as MasterKey).toLowerCase()} first`
                                : isNext
                                  ? 'Next step'
                                  : 'Not started'}
                        </p>
                      </div>
                      {!isLoading && !blocked && (
                        <Link
                          to={master.route}
                          className={cn(
                            'shrink-0 rounded-md px-3 py-1.5 text-sm font-medium',
                            isNext ? 'bg-indigo-600 text-white hover:bg-indigo-500' : 'text-indigo-600 hover:bg-indigo-50',
                          )}
                        >
                          {isDone ? 'Manage' : 'Start'}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ol>
            </section>

            {/* Needs attention */}
            <section className={cardClass}>
              <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-gray-400" />
                <h2 className="text-sm font-semibold text-gray-900">Needs attention</h2>
              </div>
              {isLoading ? (
                <div className="space-y-3 p-4">
                  <div className="h-4 animate-pulse rounded bg-gray-100" />
                  <div className="h-4 w-2/3 animate-pulse rounded bg-gray-100" />
                </div>
              ) : attention.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <CheckCircleIcon className="mx-auto h-8 w-8 text-green-500" />
                  <p className="mt-2 text-sm font-medium text-gray-900">Nothing to follow up</p>
                  <p className="mt-1 text-sm text-gray-500">
                    {records === 0 ? 'Checks start once master data has been added.' : 'Every check on the master data passes.'}
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {attention.map((item) => (
                    <li key={item.text} className="px-4 py-3">
                      <p className="text-sm text-gray-700">{item.text}</p>
                      <Link to={item.route} className="mt-1 inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500">
                        {item.action}
                        <ChevronRightIcon className="ml-0.5 h-3.5 w-3.5" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default MasterDataDashboard;
